import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { PieChart, Pie, Cell, Label } from "recharts";
import { B2bStatsCardProps } from "@/services/organizationsApi";

const COLORS: Record<string, string> = {
  starter: "hsl(var(--chart-1))",
  business: "hsl(var(--chart-2))",
  enterprise: "hsl(var(--chart-3))",
};

const chartConfig: ChartConfig = {
  starter: { label: "Starter", color: COLORS.starter },
  business: { label: "Business", color: COLORS.business },
  enterprise: { label: "Enterprise", color: COLORS.enterprise },
};

export function B2bTierPieCard({ stats, loading, error }: B2bStatsCardProps) {
  const chartData = useMemo(() => {
    const tiers = stats?.business.tiers ?? [];
    return tiers.map((t) => ({
      name: t.tier,
      value: t.count,
      fill: COLORS[t.tier] || "hsl(var(--chart-4))",
    }));
  }, [stats]);
  const total = chartData.reduce((acc, d) => acc + d.value, 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Répartition par tier</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Chargement...</div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : total === 0 ? (
          <div className="text-sm text-muted-foreground">
            Aucune organisation entreprise
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square max-h-[250px]"
          >
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent nameKey="name" />} />
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={90}
                strokeWidth={2}
              >
                {chartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-foreground text-3xl font-bold"
                          >
                            {total}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="fill-muted-foreground text-sm"
                          >
                            entreprises
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
